/**
 * Rain, as one draw call.
 *
 * A few thousand instanced quad streaks whose positions are pure functions of
 * instance index and the wave clock — each drop hashes its own column and fall
 * phase, wraps vertically, and never touches the CPU after construction. The
 * whole system follows the camera by moving the mesh, and the drops don't
 * visibly ride along because their world position is derived from hashed
 * offsets *within* the moving volume; at rain density nobody can track an
 * individual streak between frames anyway.
 *
 * WebGPU can't size point primitives, which is why these are quads and not
 * THREE.Points. Each streak gets a random yaw; edge-on drops thin to nothing,
 * which reads as natural variation rather than a bug.
 */

import { InstancedMesh, MeshBasicNodeMaterial, PlaneGeometry, Vector3 } from 'three/webgpu'
import { cos, float, hash, instanceIndex, positionLocal, sin, uniform, vec3, vec4 } from 'three/tsl'
import { waveTimeUniform } from './waves.ts'

const COUNT = 3200
/** Horizontal extent of the rain volume, centred on the camera. */
const AREA = 220
const HEIGHT = 110
const FALL_SPEED = 30
const STREAK_LENGTH = 1.6

export class Rain {
  readonly mesh: InstancedMesh
  private readonly strength = uniform(0)

  constructor() {
    const material = new MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      fog: false,
    })
    material.side = 2 // DoubleSide

    const n = float(instanceIndex)
    const columnX = hash(n).sub(0.5).mul(AREA)
    const columnZ = hash(n.add(7919)).sub(0.5).mul(AREA)
    const phase = hash(n.add(104729)).mul(HEIGHT)
    const yaw = hash(n.add(1299709)).mul(Math.PI * 2)

    // Fall, wrapped into the volume. mod() keeps the result positive.
    const y = phase.sub(waveTimeUniform.mul(FALL_SPEED)).mod(HEIGHT)

    // The quad is authored 1 unit tall; stretch to streak length and spin
    // about Y so the sheet of rain has no preferred viewing direction.
    const local = positionLocal.mul(vec3(1, STREAK_LENGTH, 1))
    const spunX = local.x.mul(cos(yaw))
    const spunZ = local.x.mul(sin(yaw))

    material.positionNode = vec3(columnX.add(spunX), y.add(local.y), columnZ.add(spunZ))

    // Faint, slightly blue, fading in with the storm. Drops whose column sits
    // within a few metres of the camera fade out entirely — a 7 cm streak
    // sweeping past the near plane otherwise fills the screen for a frame.
    const nearFade = columnX.mul(columnX).add(columnZ.mul(columnZ)).sqrt().smoothstep(3, 10)
    material.colorNode = vec4(0.62, 0.68, 0.78, this.strength.mul(0.32).mul(nearFade))

    this.mesh = new InstancedMesh(new PlaneGeometry(0.07, 1), material, COUNT)
    this.mesh.frustumCulled = false
    this.mesh.renderOrder = 500
    this.mesh.name = 'rain'
    this.mesh.visible = false
  }

  /** Follow the camera and fade with the storm. Call once per frame. */
  update(cameraPosition: Vector3, storm: number): void {
    this.strength.value = storm
    this.mesh.visible = storm > 0.02
    // The wrapped drop range is 0..HEIGHT above the mesh origin; centre it on
    // the camera so rain surrounds the lens rather than hovering above it.
    this.mesh.position.set(cameraPosition.x, cameraPosition.y - HEIGHT * 0.5, cameraPosition.z)
  }
}
